import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../services/api";
import { 
  Container, 
  Header, 
  Logo, 
  Nav, 
  Hamburger,
  MobileMenuOverlay,
  MobileMenu,
  GrabberHandle,
  MobileMenuLogo,
  Divider,
  MenuLink,
  Main, 
  ForgotBox, 
  Title, 
  InputField, 
  SubmitButton, 
  Message, 
  LinkText, 
  ForgotPasswordLink,
  Footer,
  ErrorMessage
} from "../styles/ForgotPasswordStyles";
import logo from '../assets/logo-white.png';
import bgImage from '../assets/bgimagefinal.png';

function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);

  const handleForgotPassword = async () => {
    setIsLoading(true);
    setMessage("");
    try {
      const response = await api.post("/auth/forgot-password", { email });
      setMessage(response.data.message);
    } catch (err) {
      setMessage(err.response?.data?.detail || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleLinkClick = (e, path) => {
    const link = e.currentTarget;
    const ripple = document.createElement("span");
    ripple.classList.add("ripple");
    const rect = link.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${e.clientX - rect.left - size / 2}px`;
    ripple.style.top = `${e.clientY - rect.top - size / 2}px`;
    link.appendChild(ripple);

    if ("vibrate" in navigator) {
      navigator.vibrate(50);
    }

    setTimeout(() => {
      closeMenu();
      navigate(path);
    }, 200);
  };

  const getLinkClass = (path) => {
    return location.pathname === path ? "active" : "";
  };

  return (
    <Container bgImage={bgImage}>
      <Header>
        <a href="/">
          <Logo src={logo} alt="Nutrify Today Logo" />
        </a>
        <Nav>
          <a href="/">HOME</a>
          <a href="/about">ABOUT</a>
          <a href="/contact">CONTACT</a>
          <a href="/login">LOGIN</a>
          <a href="/signup" className="register">REGISTER</a>
        </Nav>
        <Hamburger 
          className={isMenuOpen ? 'open' : ''} 
          onClick={toggleMenu}
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && toggleMenu()}
        >
          <span></span>
          <span></span>
          <span></span>
        </Hamburger>
      </Header>
      <MobileMenuOverlay isOpen={isMenuOpen} onClick={closeMenu} />
      <MobileMenu isOpen={isMenuOpen} ref={menuRef}>
        <MobileMenuLogo src={logo} alt="Nutrify Today Logo" />
        <Divider />
        <MenuLink 
          href="/" 
          className={getLinkClass("/")} 
          onClick={(e) => handleLinkClick(e, "/")} 
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && handleLinkClick(e, "/")}
        >
          HOME
        </MenuLink>
        <MenuLink 
          href="/about" 
          className={getLinkClass("/about")} 
          onClick={(e) => handleLinkClick(e, "/about")} 
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && handleLinkClick(e, "/about")}
        >
          ABOUT
        </MenuLink>
        <MenuLink 
          href="/contact" 
          className={getLinkClass("/contact")} 
          onClick={(e) => handleLinkClick(e, "/contact")} 
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && handleLinkClick(e, "/contact")}
        >
          CONTACT
        </MenuLink>
        <MenuLink 
          href="/login" 
          className={getLinkClass("/login")} 
          onClick={(e) => handleLinkClick(e, "/login")} 
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && handleLinkClick(e, "/login")}
        >
          LOGIN
        </MenuLink>
        <MenuLink 
          href="/signup" 
          className={`register ${getLinkClass("/signup")}`} 
          onClick={(e) => handleLinkClick(e, "/signup")} 
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && handleLinkClick(e, "/signup")}
        >
          REGISTER
        </MenuLink>
        <GrabberHandle 
          onClick={closeMenu}
          aria-label="Close menu"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && closeMenu()}
        />
      </MobileMenu>
      <Main>
        <ForgotBox>
          <Title>Forgot Password</Title>
          {message && <Message>{message}</Message>}
          <form onSubmit={(e) => { e.preventDefault(); handleForgotPassword(); }}>
            <InputField
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
            />
            <SubmitButton type="submit" disabled={isLoading}>
              {isLoading ? "Submitting..." : "Submit"}
            </SubmitButton>
          </form>
          <LinkText>
            Remembered your password? <a href="/login">Login</a>
          </LinkText>
        </ForgotBox>
      </Main>
      <Footer>
        <p>© 2025 DealSphere. All rights reserved.</p>
        <div>
          <a href="#terms">Terms</a>
          <a href="#privacy">Privacy</a>
          <a href="#contact">Contact</a>
        </div>
      </Footer>
    </Container>
  );
}

export default ForgotPassword;